<template>
  <a-card title="通知中心" class="notification-page">
    <div class="toolbar">
      <a-radio-group v-model="read" button-style="solid" @change="handleFilterChange">
        <a-radio-button value="">全部</a-radio-button>
        <a-radio-button value="unread">未读</a-radio-button>
        <a-radio-button value="read">已读</a-radio-button>
      </a-radio-group>
      <a-button type="primary" icon="check" @click="handleReadAll">全部标记已读</a-button>
    </div>

    <a-list :loading="loading" :data-source="list" item-layout="vertical">
      <a-list-item
        slot="renderItem"
        slot-scope="item"
        class="notice-item"
        :class="{ 'notice-item--unread': !item.is_read, 'notice-item--read': item.is_read }"
        @click="openDetail(item)"
      >
        <span v-if="!item.is_read" class="notice-dot" aria-label="未读"></span>
        <div class="notice-item__main">
          <div class="notice-heading">
            <span class="notice-title">{{ item.title }}</span>
            <a-tag v-if="!item.is_read" color="blue" class="notice-unread-tag">未读</a-tag>
          </div>
          <p class="notice-summary">{{ item.summary || item.content || '暂无内容摘要' }}</p>
          <div class="notice-meta">
            <a-tag :color="item.level === 'warning' ? 'orange' : 'blue'">{{ typeText(item.type) }}</a-tag>
            <span>{{ item.publisher || '系统通知' }}</span>
            <span>{{ item.publish_time || '-' }}</span>
          </div>
        </div>
      </a-list-item>
    </a-list>

    <div class="pagination">
      <a-pagination
        :current="page"
        :page-size="pageSize"
        :total="total"
        show-less-items
        @change="handlePageChange"
      />
    </div>

    <a-drawer :visible="visible" :title="current.title" width="560" @close="visible = false">
      <div class="drawer-meta">
        <a-tag :color="current.level === 'warning' ? 'orange' : 'blue'">{{ typeText(current.type) }}</a-tag>
        <span>{{ current.publisher || '-' }}</span>
        <span>{{ current.publish_time || '-' }}</span>
      </div>
      <p class="content">{{ current.content || current.summary }}</p>
    </a-drawer>
  </a-card>
</template>

<script>
import {
  getNotifications,
  readAllNotifications,
  readNotification,
  readSystemNotification,
} from '@/api/notification';
import {
  emitNotificationChanged,
  onNotificationChanged,
} from '@/utils/notification-events';

export default {
  name: 'NotificationCenter',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 20,
      read: '',
      visible: false,
      current: {},
      unsubscribeNotificationChanged: null,
    };
  },
  created() {
    this.loadData();
    this.unsubscribeNotificationChanged = onNotificationChanged(() => {
      this.loadData();
    });
  },
  beforeDestroy() {
    if (this.unsubscribeNotificationChanged) {
      this.unsubscribeNotificationChanged();
    }
  },
  methods: {
    loadData() {
      this.loading = true;
      getNotifications({ page: this.page, page_size: this.pageSize, read: this.read })
        .then((res) => {
          const data = res && res.data ? res.data : {};
          const paging = data.paging || {};
          this.list = Array.isArray(data.data) ? data.data : [];
          this.total = paging.total_number || 0;
        })
        .catch(() => {
          this.$message.error('获取通知失败');
          this.list = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleFilterChange(e) {
      this.read = e && e.target ? e.target.value : this.read;
      this.page = 1;
      this.loadData();
    },
    handlePageChange(page) {
      this.page = page;
      this.loadData();
    },
    openDetail(item) {
      this.current = item;
      this.visible = true;
      if (item.source === 'notice' && !item.is_read && item.notice_id) {
        readNotification(item.notice_id).then(() => {
          item.is_read = true;
          emitNotificationChanged({ type: 'read', noticeId: item.notice_id });
        });
      } else if (item.source === 'system' && item.type === 'merchant_expiry' && !item.is_read && item.read_key) {
        readSystemNotification(item.type, item.read_key).then(() => {
          item.is_read = true;
          emitNotificationChanged({ type: 'system-read', readKey: item.read_key });
        });
      }
    },
    handleReadAll() {
      readAllNotifications().then(() => {
        this.$message.success('已全部标记为已读');
        emitNotificationChanged({ type: 'read-all' });
      });
    },
    typeText(type) {
      const map = {
        system_update: '系统更新',
        maintenance: '维护通知',
        announcement: '公告',
        merchant_expiry: '到期提醒',
      };
      return map[type] || type || '通知';
    },
  },
};
</script>

<style scoped lang="less">
.notification-page {
  margin: 16px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.notice-item {
  position: relative;
  display: flex;
  gap: 12px;
  cursor: pointer;
  padding: 18px 16px;
  border-radius: 6px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.notice-item--unread {
  background: #f5faff;
  box-shadow: inset 3px 0 0 #1890ff;
}

.notice-item--unread:hover {
  background: #eef7ff;
}

.notice-item--read {
  color: #8c8c8c;
}

.notice-item--read:hover {
  background: #fafafa;
}

.notice-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: #1890ff;
}

.notice-item__main {
  min-width: 0;
  flex: 1;
}

.notice-heading {
  display: flex;
  align-items: center;
}

.notice-title {
  overflow: hidden;
  color: #1f1f1f;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-item--read .notice-title {
  color: #595959;
  font-weight: 400;
}

.notice-unread-tag {
  flex: 0 0 auto;
  margin-left: 8px;
}

.notice-summary {
  display: -webkit-box;
  margin: 8px 0 10px;
  overflow: hidden;
  color: #595959;
  line-height: 1.6;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.notice-item--read .notice-summary {
  color: #8c8c8c;
}

.notice-meta,
.drawer-meta {
  display: flex;
  gap: 12px;
  align-items: center;
  color: #666;
  font-size: 13px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.content {
  margin-top: 16px;
  white-space: pre-wrap;
  line-height: 1.8;
}

@media (max-width: 767px) {
  .notification-page {
    margin: 8px;
  }

  .toolbar {
    align-items: stretch;
    gap: 12px;
    flex-direction: column;
  }

  .toolbar .ant-btn {
    align-self: flex-start;
  }

  .notice-item {
    padding: 16px 12px;
  }

  .notice-meta,
  .drawer-meta {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
